import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output
} from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { map, takeUntil } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

import {
  CustomIdType,
  CustomIdTypeDetailsFragment,
  EntitiesReportInput, ExportEntitiesGQL, GetCustomIdTypesGQL, GetEntityConnectionTypesGQL,
  RestrictedExportingSettingTypeDetailsFragment, TagGroupInfoFragment, UserDetailsFragment, UserRole
} from 'generated/graphql';

import { selectCurrentUser } from 'app/_state/auth/auth-selectors';
import { AuthState } from 'app/_state/auth/auth-state';
import { selectCurrentCampaign } from 'app/_state/campaign-context/campaign-context-selectors';
import { CampaignContextState } from 'app/_state/campaign-context/campaign-context-state';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { IQueryStorageItem } from 'query-builder/core/query-storage.service';
import { SimpleQueryReducer } from 'query-builder/core/simple-query-reducer';
import { GetCustomIdTypesService } from 'shared/get-custom-id-types.service';

@Component({
  selector: 'app-export-standard-component',
  templateUrl: './export-standard.component.html',
  styleUrls: ['./export-standard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportStandardComponent extends WithDisposableSubject() implements OnInit {
  loading = true;
  currentUser: UserDetailsFragment;
  restrictedExportingSettings: RestrictedExportingSettingTypeDetailsFragment;

  @Output() exportSuccess = new EventEmitter<boolean>();
  @Input() query: IQueryStorageItem;
  @Input() recordCount: number;

  identifierOptions: { value: string; label: string; disabled?: boolean}[] = [];

  selectedIds = [];

  selectedProperties = [
    'entity_type_name',
    'date_added_to_campaign',
    'name',
    'nickname',
    'emails',
    'phone_numbers',
    'most_recent_assessment'
  ];

  // used to order the export columns at the end
  exportOrder = [
    'id',
    'interact_id',
    'custom_id',
    'custom_id_1',
    'custom_id_2',
    'custom_id_3',
    'custom_id_4',
    'voterbase_id',
    'dw_id',
    'entity_type_name',
    'date_added_to_campaign',
    'name',
    'nickname',
    'date_of_birth',
    'age',
    'preferred_language', // this will get exported too when languages is selected
    'languages',
    'home_addresses',
    'emails',
    'phone_numbers',
    'social_profile',
    'most_recent_assessment',
    'most_recent_connection',
    'most_recent_notes',
    'tags'
  ];

  // This holds metadata for the properties that are selected in the UI
  // key: property name
  propertyData = {
    home_addresses: {
      count: 1,
      electoral_districts: false,
      types: 'physical,mailing'
    },
    emails: {
      count: 1,
      // Don't love this, but it works for now
      types: 'home,work',
    },
    phone_numbers: {
      count: 1,
      types: 'home,work,cell',
    },
    most_recent_connection: {
      entity_connection_type_id: null
    },
    most_recent_notes: {
      count: 1
    },
    tags: {
      tag_category_ids: []
    }
  };

  availableTagCategories: Array<{ id: string }> = [];
  connectionTypeOptions = [];

  // The select input requires an array of objects with an id property
  // It's important to only change this via the selectedCategoriesChanged function
  // this will keep it in sync with the selectedCategories array.
  // Doing this for performance reasons versus using a getter
  selectedCategories: Array<{ id: string }> = [];

  get campaignId() {
    return this.query.campaignId;
  }

  constructor(
    private _cd: ChangeDetectorRef,
    private _exportEntitiesGQL: ExportEntitiesGQL,
    private _getEntityConnectionTypesGQL: GetEntityConnectionTypesGQL,
    private _getCustomIdTypesSvc: GetCustomIdTypesService,
    private _store: Store<AuthState & CampaignContextState>
  ) {
    super();
  }

  ngOnInit() {
    this.initializeData();
  }

  buildCategoryFilterFunction() {
    const tagCategoryIds = this.availableTagCategories.map(tc => tc.id);
    const canExportTags = this.canExportProperty('tags');

    return (groups: TagGroupInfoFragment[]) => {
      if (canExportTags && tagCategoryIds.length === 0) {
        return groups;
      }

      const groupsByTag = [];
      groups.map((tagGroup) => {
        const filteredCategories = tagGroup.categories
          .filter(currentCategory => tagCategoryIds.includes(currentCategory.id));
        if (filteredCategories.length > 0) {
          groupsByTag.push({ ...tagGroup, ...{ categories: filteredCategories } });
        }
      });
      return groupsByTag;
    };
  }

  /** Used to display groups */
  canExportAnyProperty(...propertyNames: string[]) {
    if (this.currentUser.role !== UserRole.Organizer) {
      return true;
    }
    return propertyNames.some(p => this.canExportProperty(p));
  }

  /** Used to display or hide properties */
  canExportProperty(propertyName: string) {
    if (propertyName === 'most_recent_connection' && this.connectionTypeOptions.length === 0) {
      return false;
    }

    if (this.currentUser.role !== UserRole.Organizer) {
      return true;
    }

    // Allow 'languages' when 'preferred_language' is permitted to preserve existing permissions
    const allowed = [
      ...this.restrictedExportingSettings.properties,
      ['entity_type_name', 'date_added_to_campaign'] // always allowed
    ].map(t => t.toString());

    if (propertyName === 'languages') {
      return allowed.includes('languages') || allowed.includes('preferred_language');
    }

    return allowed.includes(propertyName);
  }

  getConnectionTypeById(id: string) {
    return this.connectionTypeOptions?.find(c => c.id === id);
  }

  getExportBestTooltip(cType: 'email' | 'phone' | 'address') {
    let contactType = '';
    if(cType === 'email') {
      contactType = 'email address';
    } else if (cType === 'phone') {
      contactType = 'phone number';
    } else {
      contactType = 'address';
    }

    const msg = `By default, Action Builder will export the “best” ${contactType} among all available ${contactType} types. ` +
      `Choose an option here to force Action Builder to only choose from a certain type.`;
    return msg;
  }

  onRunStandardExportClick() {
    const input: EntitiesReportInput = this._makeMutationInput();

    this._executeMutation(input).subscribe({
      next: (response) => {
        const wasSuccess = response.data?.entitiesReport.success;
        this.exportSuccess.emit(wasSuccess);
      },
      error: (err) => {
        console.error(err);
        this.exportSuccess.emit(false);
      }
    });
  }

  /**
   * When checkbox is changed, we can assume the value is being added if it exists
   * or removed if it doesn't.
   */
  propertiesCheckboxChanged(value: string) {
    if (this.selectedProperties.includes(value)) {
      this.selectedProperties = this.selectedProperties.filter(c => c !== value);
    } else {
      this.selectedProperties = [...this.selectedProperties, value];
      // A bit hacky, but whatever
      if (value === 'most_recent_connection') {
        this.propertyData.most_recent_connection.entity_connection_type_id = this.connectionTypeOptions[0].id;
        this._cd.markForCheck();
      }
    }
  }

  selectAllFieldsChanged(event) {
    return !event.checked ? this.selectedCategoriesChanged([])
      : this.selectedCategoriesChanged(this.availableTagCategories);
  }

  selectedCategoriesChanged(event: { id: string }[]) {
    this.selectedCategories = event || [];
    this.propertyData.tags.tag_category_ids = this.selectedCategories.map(c => c.id);
    const areCategoriesSelected = this.propertyData.tags.tag_category_ids.length > 0;
    if (areCategoriesSelected) {
      this._addSelectedPropertyIfNotExists('tags');
    } else {
      this.selectedProperties = this.selectedProperties.filter(p => p !== 'tags');
    }

    this._cd.markForCheck();
  }

  trackByName(_, item) {
    return item.name;
  }

  isInvalid() {
    return false;
  }

  private initializeData(): void {
    const userAndCampaign$ = this._store.select(
      createSelector(selectCurrentUser, selectCurrentCampaign, (user, campaign) => ({ user, campaign }))
    );

    const entityConnectionTypes$ = this._getEntityConnectionTypesGQL.fetch({ campaignId: this.query.campaignId })
      .pipe(
        map(response => response.data.getEntityConnectionTypes)
      );

    const customIdTypes$ = this._getCustomIdTypesSvc.getCustomIdTypes();

    combineLatest([userAndCampaign$, entityConnectionTypes$, customIdTypes$])
      .pipe(takeUntil(this.disposer))
      .subscribe(([userCampaignData, entityConnectionTypes, customIdTypes]) => {
        const { user, campaign } = userCampaignData;

        this.currentUser = user;
        this.configureIdOptions(customIdTypes);
        this.restrictedExportingSettings = campaign.restrictedExportingSettings;
        this.connectionTypeOptions = entityConnectionTypes;

        if (user.role === UserRole.Organizer) {
          this.availableTagCategories = this.restrictedExportingSettings.tagCategoryIds.map(id => ({ id }));
        }

        // Update the selected options to remove anything that shouldn't be available
        this.selectedProperties = this.selectedProperties.filter(p => this.canExportProperty(p));

        this.loading = false;
        this._cd.markForCheck();
      });
  }

  private configureIdOptions(customIdTypes: CustomIdTypeDetailsFragment[]) {
    this.identifierOptions =
    [
      {value: 'interact_id', label: 'Action Builder ID', disabled: true},
    ...customIdTypes.map(c => ({ value: c.internalName, label: c.label }))
    ]
    ;
    this.selectedIds = ['interact_id'];
  }

  private _addSelectedPropertyIfNotExists(property: string) {
    if (!this.selectedProperties.includes(property)) {
      this.selectedProperties = [...this.selectedProperties, property];
    }
  }

  private _getReportFields() {
    const transformPropertyData = (input: any): any => {
      if (input?.types) {
        // Split the types string into an array
        return {
          ...input,
          types: input.types.split(',')
        };
      }

      return input;
    };

    const reportFields = [
      'id',
      ...this.selectedIds,
      ...this.selectedProperties
    ]
      .map(p => ({ field: p, data: transformPropertyData(this.propertyData[p]) }));

    const orderedFields = reportFields.map(a => a).sort((a, b) => {
      const aIndex = this.exportOrder.indexOf(a.field);
      const bIndex = this.exportOrder.indexOf(b.field);
      return aIndex - bIndex;
    });

    // If we are exporting languages, ensure preferred_language is also included
    const langIndex = orderedFields.findIndex(f => f.field === 'languages');
    if (langIndex !== -1 && !orderedFields.find(f => f.field === 'preferred_language')) {
      const prefLangField = { field: 'preferred_language', data: {} };
      orderedFields.splice(langIndex, 0, prefLangField);
    }

    return orderedFields;
  }

  private _makeMutationInput(): EntitiesReportInput {
    const reducedQuery = new SimpleQueryReducer(this.query.query).reduce();
    const fields = this._getReportFields();
    return {
      params: {
        reportType: 'standard',
        campaignId: this.query.campaignId,
        fields,
        queryJson: JSON.stringify(reducedQuery)
      }
    };
  }

  private _executeMutation(input: EntitiesReportInput) {
    return this._exportEntitiesGQL.mutate({
      input
    });
  }

}
